(() => {
  if (window.akbarDiaryPlaybackLoaded) return;
  window.akbarDiaryPlaybackLoaded = true;

  const players = new WeakMap();
  const loadingDelay = 250;
  // Warm an on-screen diary only after scrolling has settled for this long.
  const prewarmDelay = 400;
  // Below 25% visible a diary has left the screen; at 60% it is worth warming.
  const leaveRatio = 0.25;
  const prewarmRatio = 0.6;
  const getScope = wrapper => wrapper.closest('[data-akbar-diary-scope], [id^="gallery-"]');
  const selectors = '[data-akbar-diary-scope] .video-wrapper, [id^="gallery-"] .video-wrapper';
  // Rendition choice lives in akbar-style-diaries-video-sources.js; product galleries do not load it.
  const videoSources = () => window.akbarStyleDiariesVideoSources;
  const visibilityObserver = window.IntersectionObserver
    ? new window.IntersectionObserver(onVisibilityChange, { threshold: [leaveRatio, prewarmRatio] })
    : null;

  function mountVideo(wrapper) {
    const template = wrapper.querySelector('template[data-akbar-deferred-video]');
    if (!template) return wrapper.querySelector('video');
    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector('video');
    if (!video) return null;
    if (wrapper.hasAttribute('data-akbar-responsive-poster')) {
      const poster = wrapper.querySelector('.diary-poster');
      // Reuse the selected thumbnail before insertion to avoid a second poster download.
      if (poster?.currentSrc) video.poster = poster.currentSrc;
    }
    // Choose the rendition before insertion so the browser never requests the default one.
    videoSources()?.apply(wrapper, video);
    wrapper.insertBefore(fragment, template);
    template.remove();
    return video;
  }

  function onVisibilityChange(entries) {
    for (const entry of entries) players.get(entry.target)?.onVisibility(entry.intersectionRatio);
  }

  class StyleDiaryPlayer {
    constructor(wrapper, button) {
      this.wrapper = wrapper;
      this.button = button;
      this.baseLabel = button.getAttribute('aria-label') || 'Play video';
      this.video = wrapper.querySelector('video');
      this.requestId = 0;
      this.savedSources = [];
      wrapper.dataset.playbackBound = 'true';
      players.set(wrapper, this);
      this.bindVideo();
      wrapper.addEventListener('akbar:style-diary:cancel', () => this.cancel());
      button.addEventListener('pointerdown', () => this.prime(), { passive: true });
      visibilityObserver?.observe(wrapper);
      button.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        this.requestPlay();
      });
    }

    get requested() {
      return this.wrapper.dataset.playRequested === 'true';
    }

    setState(state) {
      if (state === 'loading') {
        if (this.loadingTimer != null || this.wrapper.dataset.playbackState === 'loading') return;
        this.renderState('pending');
        this.loadingTimer = setTimeout(() => {
          this.loadingTimer = null;
          if (this.requested && this.wrapper.dataset.playbackState === 'pending') this.renderState('loading');
        }, loadingDelay);
        return;
      }
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
      this.renderState(state);
    }

    renderState(state) {
      this.wrapper.dataset.playbackState = state;
      this.button.setAttribute('aria-busy', state === 'pending' || state === 'loading' ? 'true' : 'false');
      const suffix = state === 'loading' ? ' — Loading' : state === 'error' ? ' — Tap to retry' : '';
      this.button.setAttribute('aria-label', this.baseLabel + suffix);
    }

    reset(failed = false) {
      this.requestId++;
      this.started = false;
      if (this.readyListener) this.video?.removeEventListener('canplay', this.readyListener);
      this.readyListener = null;
      if (this.frameRequest != null) this.video?.cancelVideoFrameCallback?.(this.frameRequest);
      this.frameRequest = null;
      this.wrapper.dataset.playRequested = 'false';
      this.button.classList.remove('hidden');
      this.setState(failed ? 'error' : 'idle');
    }

    reveal(requestId) {
      if (requestId !== this.requestId || !this.requested || this.video.paused) return;
      this.frameRequest = null;
      this.wrapper.dataset.frameReady = 'true';
      this.button.classList.add('hidden');
      this.setState('playing');
    }

    bindVideo() {
      const video = this.video;
      if (!video || video.dataset.playbackBound) return;
      video.dataset.playbackBound = 'true';
      const label = this.wrapper.dataset.akbarVideoLabel;
      if (label) video.setAttribute('aria-label', label);
      video.addEventListener('playing', () => this.onPlaying());
      video.addEventListener('click', () => { if (!video.paused) video.pause(); });
      video.addEventListener('waiting', () => { if (this.requested) this.setState('loading'); });
      video.addEventListener('pause', () => {
        if (this.requested && this.started && video.paused) this.reset();
      });
      video.addEventListener('ended', () => { if (video.ended) this.reset(); });
      video.addEventListener('loadedmetadata', () => this.restorePosition());
      video.addEventListener('error', () => {
        if (this.requested && video.error) this.reset(true);
      });
    }

    onPlaying() {
      if (!this.requested) { this.video.pause(); return; }
      if (this.video.paused) return;
      this.setState('playing');
      if (!this.video.requestVideoFrameCallback) {
        const requestId = this.requestId;
        requestAnimationFrame(() => requestAnimationFrame(() => this.reveal(requestId)));
      }
    }

    prime(force = false) {
      this.video = this.video || mountVideo(this.wrapper);
      this.bindVideo();
      if (!this.video) return;
      for (const [element, url] of this.savedSources) element.setAttribute('src', url);
      this.savedSources = [];
      if (this.wrapper.hasAttribute('data-akbar-unmute-on-play')) this.video.muted = false;
      if (force || !this.video.dataset.preloadPrimed) {
        this.video.dataset.preloadPrimed = 'true';
        delete this.wrapper.dataset.frameReady;
        this.video.load();
      }
    }

    onVisibility(ratio) {
      clearTimeout(this.prewarmTimer);
      // A diary leaving the screen stops like a switch to another one, so it never plays unseen.
      if (ratio < leaveRatio && (this.requested || this.wrapper.dataset.frameReady)) this.cancel();
      if (ratio >= prewarmRatio) this.prewarmTimer = setTimeout(() => this.prewarm(), prewarmDelay);
    }

    // Fetch the video's metadata once, so a later tap starts from a warm request.
    prewarm() {
      // Only rendition-aware diaries (homepage and directory) are warmed.
      if (this.prewarmed || !this.wrapper.hasAttribute('data-akbar-style-diaries-sources')) return;
      // The diary being loaded or watched has priority; warm this one on a later visit.
      if (this.otherPlayers().some(player => player.requested)) return;
      this.prewarmed = true;
      const sources = videoSources();
      if (this.requested || this.video?.dataset.preloadPrimed || !sources || sources.prefersLowData()) return;
      this.prime();
    }

    // After a suspended diary reloads, continue from where the visitor left it.
    restorePosition() {
      if (!this.resumeTime) return;
      this.video.currentTime = this.resumeTime;
      this.resumeTime = 0;
    }

    // Browsers keep buffering paused videos, so stop this download to give the selected
    // diary the bandwidth. A played diary remembers its position and shows its thumbnail.
    suspendDownload() {
      const video = this.video;
      if (!video?.dataset.preloadPrimed) return;
      if (this.wrapper.dataset.frameReady) {
        this.resumeTime = video.ended ? 0 : video.currentTime;
        delete this.wrapper.dataset.frameReady;
      }
      const sources = [video, ...video.querySelectorAll('source')];
      this.savedSources = sources.filter(source => source.hasAttribute('src'))
        .map(source => [source, source.getAttribute('src')]);
      for (const [source] of this.savedSources) source.removeAttribute('src');
      delete video.dataset.preloadPrimed;
      // Reset source selection with no URLs to abort the abandoned download.
      video.load();
    }

    cancel() {
      // Invalidate callbacks before pause/load can settle an old play promise.
      this.reset();
      this.video?.pause();
      this.suspendDownload();
    }

    otherPlayers() {
      const wrappers = getScope(this.wrapper)?.querySelectorAll('.video-wrapper') || [];
      return [...wrappers].filter(wrapper => wrapper !== this.wrapper)
        .map(wrapper => players.get(wrapper)).filter(Boolean);
    }

    cancelOthers() {
      this.otherPlayers().forEach(player => player.cancel());
    }

    start(requestId) {
      if (requestId !== this.requestId || !this.requested) return;
      this.readyListener = null;
      this.started = true;
      if (this.video.requestVideoFrameCallback) {
        this.frameRequest = this.video.requestVideoFrameCallback(() => this.reveal(requestId));
      }
      this.video.play().catch(() => {
        if (requestId === this.requestId && this.requested) this.reset(true);
      });
    }

    requestPlay() {
      if (this.requested) return;
      // A chosen diary no longer needs warming; avoid reloading it in the background later.
      this.prewarmed = true;
      this.cancelOthers();
      const retry = this.wrapper.dataset.playbackState === 'error' || Boolean(this.video?.error);
      this.prime(retry);
      if (!this.video) { this.reset(true); return; }
      const requestId = ++this.requestId;
      this.wrapper.dataset.playRequested = 'true';
      this.setState('loading');
      if (this.video.readyState >= 2) {
        this.start(requestId);
      } else {
        this.readyListener = () => this.start(requestId);
        this.video.addEventListener('canplay', this.readyListener, { once: true });
      }
    }
  }

  function initialize() {
    document.querySelectorAll(selectors).forEach(wrapper => {
      if (wrapper.dataset.playbackBound) return;
      const button = wrapper.querySelector('.video-play-btn');
      if (button) new StyleDiaryPlayer(wrapper, button);
    });
  }

  initialize();
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('akbar:style-diaries:updated', initialize);
})();
